import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Globe, Check, ChevronDown } from 'lucide-react';
import { useLanguage, languageNames } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';

const LanguageSelector = ({ compact = false }) => {
  const { language, setLanguage, languages } = useLanguage();
  const { theme } = useTheme();
  const [isOpen, setIsOpen] = useState(false);

  const currentLang = languageNames[language];

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 rounded-xl transition-all duration-200 ${
          compact ? 'p-2' : 'px-4 py-2.5'
        }`}
        style={{
          background: 'rgba(255,255,255,0.08)',
          border: `1px solid ${theme.colors.border}`,
          color: theme.colors.text
        }}
        data-testid="language-selector-btn"
      >
        <Globe className="w-5 h-5" style={{ color: theme.colors.primary }} />
        {!compact && (
          <>
            <span className="text-sm font-medium">{currentLang.flag} {currentLang.name}</span>
            <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </>
        )}
      </button>

      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-40"
              onClick={() => setIsOpen(false)}
            />
            
            {/* Dropdown */}
            <motion.div
              initial={{ opacity: 0, y: -10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.95 }}
              transition={{ duration: 0.15 }}
              className="absolute right-0 mt-2 z-50 min-w-[200px] rounded-2xl overflow-hidden"
              style={{
                background: 'rgba(10, 15, 30, 0.98)',
                backdropFilter: 'blur(20px)',
                border: `1px solid ${theme.colors.border}`,
                boxShadow: '0 20px 50px rgba(0,0,0,0.5)'
              }}
              data-testid="language-dropdown"
            >
              <div className="py-2 max-h-[400px] overflow-y-auto">
                {languages.map((lang) => {
                  const langInfo = languageNames[lang];
                  const isActive = language === lang;
                  
                  return (
                    <button
                      key={lang}
                      onClick={() => {
                        setLanguage(lang);
                        setIsOpen(false);
                      }}
                      className="w-full flex items-center gap-3 px-4 py-3 transition-colors"
                      style={{
                        background: isActive ? `${theme.colors.primary}20` : 'transparent',
                        color: isActive ? theme.colors.primary : theme.colors.text
                      }}
                      data-testid={`language-option-${lang}`}
                    >
                      <span className="text-xl">{langInfo.flag}</span>
                      <span className="flex-1 text-left text-sm font-medium">{langInfo.name}</span>
                      {isActive && (
                        <Check className="w-4 h-4" style={{ color: theme.colors.primary }} />
                      )}
                    </button>
                  );
                })}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LanguageSelector;
